// ============================================
// SCROLLPORTTABLA.JS — ventana con scroll para tablas anchas (EXPEDIENTES-RESUMEN, FRENTE A)
// ============================================
// EL BUG QUE ARREGLA (2026-09-17, Expedientes > vista Tabla)
// La tabla de expedientes se salia 492 px por la derecha y no habia forma de llegar a las ultimas
// columnas (Monto, Fecha, "Ver expediente"). Medido en navegador real con 168 expedientes:
//     contenedor: clientWidth 974  scrollWidth 1466   -> si habia contenido oculto
//     contenedor: overflowX 'auto'                    -> si era desplazable
//     contenedor: alto 12,722 px, su borde inferior en y = 13,379
//     ventana:    688 px de alto
// Es decir: la barra de scroll horizontal SI existia, pero vive pegada al borde INFERIOR de su
// caja, y esa caja media 12,722 px porque crecia con las 168 filas. La barra quedaba 12,691 px por
// debajo de la pantalla. Inalcanzable.
//
// ES EL MISMO DEFECTO QUE YA SE ARREGLO EN EL KANBAN (KANBAN-SCROLL, 2026-09-10) y que aqui nadie
// toco. Por eso, ademas de arreglarlo, el sprint amplia `scripts/portero-modales.js` para que un
// tercer sitio con este patron NO pueda volver a nacer.
//
// LA SOLUCION: la caja deja de crecer con el contenido y se ACOTA al alto que queda de pantalla.
// Con eso la barra horizontal esta SIEMPRE a la vista, y las filas se recorren con el scroll
// vertical de la propia caja (la cabecera se queda pegada arriba). Ademas se le ponen las mismas
// afordancias que al Kanban, porque una barra sola no basta para que alguien entienda que hay mas
// columnas: sombra en el borde con contenido por delante, arrastre con el raton sobre el fondo y
// rueda con Shift.
//
// EN CELULAR NO SE USA: la lista se dibuja como tarjetas (no hay tabla que desplazar). Aun asi el
// alto se expresa con `calc(var(--beep-vh) * N)` y nunca con `vh` pelado, por la regla de
// CLAUDE.md que nacio del bug de PERDIDA DE DATOS del 2026-09-12.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Hueco que se deja bajo la caja para que la barra horizontal no quede pegada al borde de la
// ventana (y para que se vea que la tabla se acaba ahi).
const MARGEN_INFERIOR = 18;
// Tope de alto. En PC VERTICAL (1080x1920) una caja de ~1,700 px obliga a barrer la pantalla con
// la vista de arriba abajo; con el tope se queda en una franja comoda.
const TOPE_ALTO = 900;
// Suelo: por debajo de esto la caja seria una rendija inservible.
const ALTO_MINIMO = 260;

export default function ScrollportTabla({
  children,
  // Alto fijo opcional (celular / casos donde medir contra la ventana no aplica).
  altoFijo = null,
  topeAlto = TOPE_ALTO,
  margenInferior = MARGEN_INFERIOR,
  estilo = {},
  className = '',
}) {
  const ref = useRef(null);
  const [alto, setAlto] = useState(null);
  const [bordes, setBordes] = useState({ izq: false, der: false });
  const [arrastrando, setArrastrando] = useState(false);

  // Alto = el MENOR entre lo que queda de pantalla y lo que el contenido necesita de verdad.
  // Si la tabla es corta, la caja se queda corta: no se deja un hueco blanco debajo (fue el
  // hallazgo de Dona Frida en el Kanban, y aqui aplica igual).
  const medirAlto = useCallback(() => {
    const el = ref.current;
    if (!el || altoFijo) return;
    const top = el.getBoundingClientRect().top;
    const ventana = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    const disponible = Math.round(ventana - top - margenInferior);
    // Alto natural del contenido: el scrollHeight del hijo, que NO esta acotado por nosotros.
    const hijo = el.firstElementChild;
    const contenido = hijo ? hijo.offsetHeight : 0;
    const objetivo = contenido > 0
      ? Math.min(disponible, contenido + 2, topeAlto)   // +2: bordes de la caja
      : Math.min(disponible, topeAlto);
    setAlto(Math.max(ALTO_MINIMO, objetivo));
  }, [altoFijo, margenInferior, topeAlto]);

  // ¿Queda contenido a izquierda / derecha? Decide las sombras de borde.
  const medirBordes = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const izq = el.scrollLeft > 2;
    const der = el.scrollLeft < max - 2;
    setBordes((prev) => (prev.izq === izq && prev.der === der ? prev : { izq, der }));
  }, []);

  useLayoutEffect(() => { medirAlto(); medirBordes(); }, [medirAlto, medirBordes, children]);
  useEffect(() => {
    const alCambiar = () => { medirAlto(); medirBordes(); };
    window.addEventListener('resize', alCambiar);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', alCambiar);
    return () => {
      window.removeEventListener('resize', alCambiar);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', alCambiar);
    };
  }, [medirAlto, medirBordes]);

  // Rueda con Shift = avanzar de lado (atajo estandar de tablas y tableros).
  const alRodar = useCallback((ev) => {
    const el = ref.current;
    if (!el || !ev.shiftKey) return;
    el.scrollLeft += ev.deltaY;
  }, []);

  // Arrastre con el raton SOBRE EL FONDO. Nunca sobre una fila clicable ni sobre un control: asi
  // no se pisa el clic que abre el expediente.
  const alPresionar = useCallback((ev) => {
    const el = ref.current;
    if (!el || ev.button !== 0) return;
    if (ev.target.closest('button, a, input, select, textarea, [contenteditable="true"]')) return;
    const xInicial = ev.clientX;
    const scrollInicial = el.scrollLeft;
    let movido = false;
    const mover = (e2) => {
      const dx = e2.clientX - xInicial;
      if (!movido && Math.abs(dx) > 4) { movido = true; setArrastrando(true); }
      if (movido) { el.scrollLeft = scrollInicial - dx; medirBordes(); }
    };
    const soltar = () => {
      setArrastrando(false);
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
    };
    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
  }, [medirBordes]);

  const sombra = (lado) => ({
    position: 'absolute', top: 0, bottom: 14, width: 30, pointerEvents: 'none', zIndex: 3,
    [lado === 'izq' ? 'left' : 'right']: 0,
    borderRadius: lado === 'izq' ? '10px 0 0 10px' : '0 10px 10px 0',
    background: `linear-gradient(to ${lado === 'izq' ? 'right' : 'left'}, rgba(31,41,55,0.13), rgba(31,41,55,0))`,
  });

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={ref}
        className={`beep-scrollport-tabla ${className}`.trim()}
        onScroll={medirBordes}
        onWheel={alRodar}
        onMouseDown={alPresionar}
        style={{
          // La caja ACOTADA: esto es lo que baja la barra horizontal a la zona visible.
          height: altoFijo || (alto ? `${alto}px` : undefined),
          overflowX: 'auto', overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain',
          cursor: arrastrando ? 'grabbing' : undefined,
          ...estilo,
        }}
      >
        {children}
      </div>
      {bordes.izq && <div aria-hidden style={sombra('izq')} />}
      {bordes.der && <div aria-hidden style={sombra('der')} />}
    </div>
  );
}
